import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import FacebookAdsService, {
  FacebookConnection,
  FacebookAdSet,
  FacebookAd,
} from '../../services/facebookAdsService';

const FacebookAdsAds: React.FC = () => {
  const [connections, setConnections] = useState<FacebookConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<FacebookConnection | null>(null);
  const [adSets, setAdSets] = useState<FacebookAdSet[]>([]);
  const [selectedAdSet, setSelectedAdSet] = useState<string>('all');
  const [ads, setAds] = useState<FacebookAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    if (selectedConnection) {
      loadAdSets();
    }
  }, [selectedConnection]);

  useEffect(() => {
    if (selectedConnection && selectedAdSet !== 'all') {
      loadAds();
    } else if (selectedAdSet === 'all' && selectedConnection && adSets.length > 0) {
      loadAllAds();
    }
  }, [selectedAdSet, selectedConnection, adSets]);

  const loadConnections = async () => {
    try {
      const response = await FacebookAdsService.listConnections();
      if (response.success && response.data) {
        setConnections(response.data);
        if (response.data.length > 0 && !selectedConnection) {
          setSelectedConnection(response.data[0]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAdSets = async () => {
    if (!selectedConnection) return;

    try {
      // Buscar todas as campanhas primeiro
      const campaignsResponse = await FacebookAdsService.listCampaigns(selectedConnection.account_id.toString());
      if (campaignsResponse.success && campaignsResponse.data) {
        const allAdSets: FacebookAdSet[] = [];
        for (const campaign of campaignsResponse.data) {
          const adSetsResponse = await FacebookAdsService.getAdSets(campaign.id.toString());
          if (adSetsResponse.success && adSetsResponse.data) {
            allAdSets.push(...adSetsResponse.data);
          }
        }
        setAdSets(allAdSets);
        // Se "Todos os AdSets" estiver selecionado, carregar todos os ads
        if (selectedAdSet === 'all') {
          setTimeout(() => loadAllAds(), 100);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar adsets:', error);
    }
  };

  const loadAds = async () => {
    if (!selectedConnection || selectedAdSet === 'all') return;

    setLoading(true);
    try {
      const response = await FacebookAdsService.getAds(selectedAdSet);
      if (response.success && response.data) {
        setAds(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar anúncios:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllAds = async () => {
    if (!selectedConnection) return;

    setLoading(true);
    try {
      // Se adSets ainda não estiver carregado, carregar primeiro
      let adSetsToUse = adSets;
      if (adSetsToUse.length === 0) {
        const campaignsResponse = await FacebookAdsService.listCampaigns(selectedConnection.account_id.toString());
        if (campaignsResponse.success && campaignsResponse.data) {
          const allAdSets: FacebookAdSet[] = [];
          for (const campaign of campaignsResponse.data) {
            const adSetsResponse = await FacebookAdsService.getAdSets(campaign.id.toString());
            if (adSetsResponse.success && adSetsResponse.data) {
              allAdSets.push(...adSetsResponse.data);
            }
          }
          adSetsToUse = allAdSets;
          setAdSets(adSetsToUse);
        }
      }

      const allAds: FacebookAd[] = [];
      for (const adset of adSetsToUse) {
        const response = await FacebookAdsService.getAds(adset.id.toString());
        if (response.success && response.data) {
          allAds.push(...response.data);
        }
      }
      setAds(allAds);
    } catch (error) {
      console.error('Erro ao carregar anúncios:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar anúncios', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (adId: number) => {
    try {
      const response = await FacebookAdsService.pauseAd(adId.toString());
      if (response.success) {
        setSnackbar({ open: true, message: 'Anúncio pausado', severity: 'success' });
        if (selectedAdSet === 'all') {
          loadAllAds();
        } else {
          loadAds();
        }
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleActivate = async (adId: number) => {
    try {
      const response = await FacebookAdsService.activateAd(adId.toString());
      if (response.success) {
        setSnackbar({ open: true, message: 'Anúncio ativado', severity: 'success' });
        if (selectedAdSet === 'all') {
          loadAllAds();
        } else {
          loadAds();
        }
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleDelete = async (adId: number) => {
    if (!window.confirm('Tem certeza que deseja deletar este anúncio?')) return;

    try {
      const response = await FacebookAdsService.deleteAd(adId.toString());
      if (response.success) {
        setSnackbar({ open: true, message: 'Anúncio deletado', severity: 'success' });
        if (selectedAdSet === 'all') {
          loadAllAds();
        } else {
          loadAds();
        }
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleArchive = async (adId: number) => {
    try {
      const response = await FacebookAdsService.archiveAd(adId.toString());
      if (response.success) {
        setSnackbar({ open: true, message: 'Anúncio arquivado', severity: 'success' });
        if (selectedAdSet === 'all') {
          loadAllAds();
        } else {
          loadAds();
        }
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleDuplicate = async (adId: number, name: string) => {
    const newName = prompt(`Nome para a cópia de "${name}":`);
    if (!newName || !newName.trim()) return;

    try {
      const response = await FacebookAdsService.duplicateAd(adId.toString(), newName.trim());
      if (response.success) {
        setSnackbar({ open: true, message: 'Anúncio duplicado', severity: 'success' });
        if (selectedAdSet === 'all') {
          loadAllAds();
        } else {
          loadAds();
        }
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE': return 'success';
      case 'PAUSED': return 'warning';
      case 'DELETED': case 'DISAPPROVED': return 'error';
      case 'ARCHIVED': return 'default';
      default: return 'default';
    }
  };

  if (loading && ads.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Anúncios
        </Typography>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Conta</InputLabel>
            <Select
              value={selectedConnection?.account_id || ''}
              onChange={(e) => {
                const value = e.target.value;
                const conn = connections.find((c) => c.account_id === parseInt(value.toString()));
                setSelectedConnection(conn || null);
              }}
              label="Conta"
            >
              {connections.map((conn) => (
                <MenuItem key={conn.account_id} value={conn.account_id}>
                  {conn.account_name || `Conta ${conn.account_id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>AdSet</InputLabel>
            <Select
              value={selectedAdSet}
              onChange={(e) => setSelectedAdSet(e.target.value)}
              label="AdSet"
            >
              <MenuItem value="all">Todos os AdSets</MenuItem>
              {adSets.map((adset) => (
                <MenuItem key={adset.id} value={adset.id.toString()}>
                  {adset.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={() => {
              if (selectedAdSet === 'all') {
                loadAllAds();
              } else {
                loadAds();
              }
            }} 
            disabled={loading}
          >
            Atualizar
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Anúncio</TableCell>
              <TableCell align="right">Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ads.map((ad) => (
              <TableRow key={ad.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {ad.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {ad.id}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip label={ad.status} color={getStatusColor(ad.status) as any} size="small" />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {ad.status === 'ACTIVE' ? (
                      <Tooltip title="Pausar">
                        <IconButton size="small" onClick={() => handlePause(ad.id)} color="warning">
                          <PauseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Ativar">
                        <IconButton size="small" onClick={() => handleActivate(ad.id)} color="success">
                          <PlayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Duplicar">
                      <IconButton size="small" onClick={() => handleDuplicate(ad.id, ad.name)} color="primary">
                        <DuplicateIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Arquivar">
                      <IconButton size="small" onClick={() => handleArchive(ad.id)} color="default">
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar">
                      <IconButton size="small" onClick={() => handleDelete(ad.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {ads.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography color="text.secondary">Nenhum anúncio encontrado</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacebookAdsAds;
